import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { CreateFab } from '../../components/CreateFab';
import { PageHeader } from '../../components/PageHeader';
import { PersonRow } from '../../components/PersonRow';
import { PostCard } from '../../components/PostCard';
import { PulseRail } from '../../components/PulseRail';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ChallengeCard } from '../../components/engagement/ChallengeCard';
import { findCommunity } from '../../data/communities';
import { communityChallenges } from '../../data/engagement';
import { people } from '../../data/people';
import { useLocale } from '../../contexts/LocaleContext';
import { useSocial } from '../../contexts/SocialContext';
import { useScreenInit } from '../../useScreenInit.js';
type Tab = 'posts' | 'pulse' | 'challenges' | 'members';

/** MOB/Community/Detail — an interest space: feed, Pulse and members. */
export function CommunityDetail() {
  const {
    communityId = ''
  } = useParams();
  const {
    t,
    tl,
    locale
  } = useLocale();
  const social = useSocial();
  const screenInit = useScreenInit() as {
    detailTab?: Tab;
  };
  const [tab, setTab] = useState<Tab>(screenInit.detailTab ?? 'posts');
  const community = findCommunity(communityId);
  if (!community) {
    return <div className="px-2 pb-2" data-id="element-2184" data-mp="3hdtw80zlvn5e">
        <PageHeader title={t('community.title')} showBack data-id="element-2185" data-mp="3mdzrrs8zxtsr" />
        <EmptyState title={t('community.missing')} body={t('community.none.body')} data-id="element-2186" data-mp="3mfj7nhqqq2u0" />
      </div>;
  }
  const inCommunity = (category: string) => community.categories.some(item => item === category);
  const posts = social.posts.filter(post => inCommunity(post.context.category));
  const pulses = social.pulses.filter(ring => ring.items.some(item => inCommunity(item.kind)));
  const members = people.filter(person => person.id !== 'me' && inCommunity(person.category));
  const member = social.isMember(community.id);
  const challenges = communityChallenges.filter(item => item.communityId === community.id);
  return <div className="pb-2" data-id="element-2187" data-mp="106bqqy5o3jyq">
      <PageHeader title={tl(community.name)} showBack data-id="element-2188" data-mp="107muixz44rpu" />

      <div className="px-2" data-id="element-2189" data-mp="v80eyhvtazdv">
        <div className="relative overflow-hidden rounded-panel" data-id="element-2190" data-mp="v8sdo1kphd8d">
          <img src={community.cover} alt="" aria-hidden="true" className="aspect-[16/7] w-full object-cover" data-id="element-2191" data-mp="1sv97uxy57apw" />
          <span className="absolute inset-0 bg-deep/55" aria-hidden="true" data-id="element-2192" data-mp="1nyisydy2ql62" />
          <div className="absolute inset-0 flex flex-col justify-end p-2" data-id="element-2193" data-mp="1j4ehpihrzqz6">
            <p className="bee-num text-[11px] font-medium text-ondark" data-id="element-2194" data-mp="1e7ecf9sr2s94">
              {t('community.members', {
              n: social.memberCount(community.id).toLocaleString(locale)
            })}{' '}
              · {t('community.weekly', {
              n: community.postsThisWeek
            })}
            </p>
            <p className="mt-[2px] max-w-[36ch] text-[13px] leading-snug text-sand" data-id="element-2195" data-mp="2e785l29ngqf8">
              {tl(community.description)}
            </p>
          </div>
        </div>

        <Button variant={member ? 'outline' : 'primary'} size="lg" fullWidth className="mt-2" onClick={() => social.toggleMembership(community.id)} aria-pressed={member} data-id="element-2196" data-mp="2o4mbi7xy5wta">
          {member && <CheckIcon size={16} className="text-gold-deep" data-id="element-2197" data-mp="2x4rgycjkyl4t" />}
          {member ? t('community.joined') : t('community.join')}
        </Button>

        <div className="mt-3" data-id="element-2198" data-mp="36v3x7skd3s55">
          <SegmentedTabs label={tl(community.name)} value={tab} onChange={setTab} tabs={[{
          id: 'posts',
          label: t('community.detail.posts')
        }, {
          id: 'pulse',
          label: t('community.detail.pulse')
        }, {
          id: 'challenges',
          label: t('eng.ch.tab')
        }, {
          id: 'members',
          label: t('community.detail.members')
        }]} data-id="element-2199" data-mp="36uu9ycqpbz35" />
        </div>

        <div className="mt-3" data-id="element-2200" data-mp="mc9c8t1p30tu">
          {tab === 'posts' && (posts.length === 0 ? <EmptyState title={t('community.noPosts.title')} body={t('community.noPosts.body')} data-id="element-2201" data-mp="hd6gy0bio8z2" /> : <ul className="space-y-3" data-id="element-2202" data-mp="r6djldevhavg">
                {posts.map(post => <li key={post.id} data-id="element-2203" data-mp="3eq1nkzl9a5b">
                    <PostCard post={post} data-id="element-2204" data-mp="3frmgh6epb08" />
                  </li>)}
              </ul>)}

          {tab === 'pulse' && (pulses.length === 0 ? <EmptyState title={t('community.noPulse.title')} body={t('community.noPulse.body')} data-id="element-2205" data-mp="chr5tg4w8r6c" /> : <PulseRail pulses={pulses} data-id="element-2206" data-mp="1nutkg5nfn6nv" />)}

          {tab === 'challenges' && (challenges.length === 0 ? <EmptyState title={t('eng.ch.none')} body={t('eng.ch.noneBody')} data-id="element-2207" data-mp="1ivqfsmbd72gf" /> : <section aria-label={t('eng.ch.weekly')} data-id="element-2208" data-mp="1rbpgaoaya35l">
                <p className="mb-2 text-[11.5px] leading-snug text-muted" data-id="element-2209" data-mp="1vfdn34rrcups">{t('eng.ch.optional')}</p>
                <ul className="space-y-2" data-id="element-2210" data-mp="1hfmdrhc6s2i4">
                  {challenges.map(challenge => <li key={challenge.id} data-id="element-2211" data-mp="1mdnwu29m41tl">
                      <ChallengeCard challenge={challenge} data-id="element-2212" data-mp="1mepit1s628q3" />
                    </li>)}
                </ul>
              </section>)}

          {tab === 'members' && <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2213" data-mp="2f2bp4hss3rgn">
              {members.map(person => <PersonRow key={person.id} person={person} data-id="element-2214" data-mp="20c4e2n53papz" />)}
            </ul>}
        </div>
      </div>

      <CreateFab data-id="element-2215" data-mp="2t24b5ztd1jia" />
    </div>;
}